import { useState } from "react";
import { Loader2, Lock, Users } from "lucide-react";
import { Label } from "@/components/ui/label";
import { MoneyInput } from "@/components/common/MoneyInput";
import { TooltipButton } from "@/components/common/Tooltips";
import { mxn } from "@/lib/format";
import { ROL_LABEL, sumaMontos } from "@/lib/integrantesGrupo";
import { CuadreAutorizado, diferencia } from "@/components/solicitudes/CuadreAutorizado";

/** Contratación: integrantes precargados de la solicitud; solo se ajustan los montos. */
export function IntegrantesContratacion({ info, busy, puedeContratar, onContratar }) {
  const [montos, setMontos] = useState(() =>
    Object.fromEntries(info.integrantes.map((i) => [i.cliente_id, i.monto ?? ""])));
  const filas = info.integrantes.map((i) => ({ ...i, monto: montos[i.cliente_id] }));
  const suma = sumaMontos(filas);
  const autorizado = Number(info.monto_autorizado) || 0;
  const valido = autorizado > 0 && diferencia(suma, autorizado) === 0 && filas.every((i) => parseFloat(i.monto) > 0);

  const contratar = () => onContratar(filas.map((i) => ({ cliente_id: i.cliente_id, monto: parseFloat(i.monto) || 0 })));

  return (
    <div data-testid="integrantes-contratacion">
      <div className="space-y-2">
        {filas.map((it, idx) => (
          <div key={it.cliente_id} className="grid grid-cols-1 items-end gap-2 rounded-md border border-slate-200 bg-white p-2 sm:grid-cols-12"
            data-testid={`integrante-row-${idx}`}>
            <div className="sm:col-span-6">
              <Label className="mb-1 block text-[10px] uppercase text-slate-400">Cliente</Label>
              <p className="flex items-center gap-1.5 text-sm font-medium text-slate-800" data-testid={`integrante-cliente-fijo-${idx}`}>
                <Lock className="h-3.5 w-3.5 text-slate-400" /> {it.cliente || it.cliente_id}
              </p>
              <p className="text-[11px] text-slate-500">{ROL_LABEL[it.rol] || it.rol} · tasa {it.tasa_ordinaria ?? "del producto"}{it.tasa_ordinaria != null ? "%" : ""}</p>
            </div>
            <div className="sm:col-span-3">
              <Label className="mb-1 block text-[10px] uppercase text-slate-400">Monto</Label>
              <MoneyInput data-testid={`integrante-monto-${idx}`} value={it.monto}
                onChange={(v) => setMontos((p) => ({ ...p, [it.cliente_id]: v }))} />
            </div>
            <p className="text-[11px] text-slate-500 sm:col-span-3">Solicitado: {mxn(info.integrantes[idx].monto)}</p>
          </div>
        ))}
      </div>
      <CuadreAutorizado suma={suma} autorizado={autorizado} />
      <p className="mt-2 text-[11px] text-slate-500">
        El grupo es el que revisó el Comité: no se agregan ni se quitan integrantes. Solo ajusta los montos para que la suma sea igual al monto autorizado.
      </p>
      <TooltipButton tooltip="Genera un crédito grupal con obligados solidarios" data-testid="contratar-grupal-btn"
        onClick={contratar} disabled={!valido || busy || !puedeContratar} className="mt-3 bg-accent hover:bg-forest-700">
        {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Users className="mr-1.5 h-4 w-4" />}
        Validar grupo y contratar crédito grupal
      </TooltipButton>
    </div>
  );
}
